/* eslint-disable react/no-danger */
/* eslint-disable react/jsx-props-no-spreading */
import {
  fetchQuestionAttemptRequest,
  updateQuestionAttemptRequest,
} from 'APIs/letrusAPI/questionnaire';
import {
  QuestionAttempt,
  QuestionnaireStep,
} from 'APIs/letrusAPI/questionnaire/types';
import {Button, TextArea, useModals} from 'letrus-ui';
import {useForm} from 'react-hook-form';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {useQueryParam} from 'utils/hooks';
import GeneralErrorModal from 'components/GeneralErrorModal';
import {useEffect, useState} from 'react';
import {isFieldEmpty} from 'utils/functions/validation/emptyField';
import useTrackEvent from 'utils/hooks/useTrackEvent';
import {timerSetup} from 'utils/functions/timer';
import {useQuestion} from 'containers/NewQuestionnaire/hooks/useQuestion';
import {getOpenQuestionFeedbackCopy} from 'containers/NewQuestionnaire/hooks/useQuestion/openQuestionFeedbackCopy';
import styles from './Question.module.scss';
import Choice from '../Choice';
import IconRating from '../IconRating';

interface QuestionForm {
  answer?: string;
  responses?: string | string[];
}

export interface QuestionProps {
  onReturn?(): void;
  isFirstQuestion?: boolean;
  learningPathStepId?: number;
  onAdvance?(answer: string): void;
  question?: QuestionnaireStep['question'];
}

function Question({
  question,
  onReturn,
  onAdvance,
  isFirstQuestion,
  learningPathStepId,
}: QuestionProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionTimer = timerSetup();
  const queryParam = useQueryParam();
  const {trackEvent} = useTrackEvent();
  const learningPathUserInstanceId = queryParam.get('trilha') ?? '';

  const {user} = getUserData();
  const modals = useModals(['error', 'submitError']);
  const form = useForm<QuestionForm>({mode: 'onChange'});

  const updateQuestionAttempt = updateQuestionAttemptRequest();
  const questionAttempts = fetchQuestionAttemptRequest({
    user: user?.id,
    question: question?.id,
    step: learningPathStepId,
    ...(learningPathUserInstanceId
      ? {learning_path_user_instance: learningPathUserInstanceId}
      : {}),
  });

  const {
    userChoices,
    userAnswer,
    hasFeedback,
    hasBeenAnswered,
    generalFeedback,
    hasGeneralFeedback,
  } = useQuestion({question, attempts: questionAttempts.data});

  // `userAnswer` only exists after `questionAttempts` (async) resolves — since the text field's
  // `defaultValue` is only read on mount, an already-saved answer that arrives AFTER the first
  // render (same `question.id`, without remounting) wouldn't show up in the field (Copilot
  // finding on the PR, 2026-09-22). `setValue` writes to the field without making it controlled —
  // it doesn't fight with what the student is already typing.
  useEffect(() => {
    if (userAnswer !== undefined) {
      form.setValue('answer', userAnswer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the async arrival of
    // userAnswer; form.setValue is stable (react-hook-form), including `form` entirely would
    // recreate the effect on every render.
  }, [userAnswer]);

  function handleQuestionUpdate(formData: QuestionForm) {
    if (isSubmitting) return;

    if (hasBeenAnswered) {
      goToNextQuestion();
      return;
    }

    setIsSubmitting(true);

    updateQuestionAttempt.trigger(
      {
        user: user?.id,
        question: question?.id,
        answer: formData.answer,
        step: learningPathStepId,
        learning_path_user_instance: learningPathUserInstanceId,
        responses: serializeResponses(formData.responses),
      },
      {
        onError: (
          error: Error & {response?: {status?: number}; status?: number},
        ) => {
          setIsSubmitting(false);
          const status = error?.response?.status || error?.status;

          if (!status || status >= 500 || status === 0) {
            modals.submitError?.showModal();
          } else {
            modals.error?.showModal();
          }
        },
        onSuccess: (answer) => {
          setIsSubmitting(false);
          updateAnswer(answer);
        },
      },
    );
  }

  function serializeResponses(responses?: string | string[]) {
    // These question types don't have responses/choices - they use 'answer' field instead
    const typesWithoutResponses = ['statement', 'text', 'rating', 'ai_tutor'];

    if (
      question?.question_type &&
      typesWithoutResponses.includes(question.question_type)
    ) {
      return undefined;
    }

    if (!responses) return undefined;

    return Array.isArray(responses)
      ? responses?.map((item) => ({option: Number(item)}))
      : [{option: Number(responses)}];
  }

  // Whether the FRESH answer just returned by the submit has feedback worth pausing on — reading
  // `hasFeedback` from the hook here would use the stale pre-submission render (no attempt existed
  // yet), which happens to still work for graded objectives (`question.has_correct_answer` is
  // known upfront) but is always false for text questions (nothing about a dissertativa is known
  // before it's answered) — so it always skipped the feedback screen regardless of what the
  // backend returned (achado real, 2026-09-28: devolutiva de dissertativa nunca pausava a tela,
  // mesmo com open_question_feedback vindo preenchido no POST).
  function answerHasFeedback(answer: QuestionAttempt): boolean {
    if (question?.question_type === 'text') {
      // Pause on the same signal that decides what to show: a usable evaluator score maps to the
      // standard copy, so a score outside 0/1/2 (or no open_question_feedback at all) shows no
      // box and must not pause the screen.
      return !!getOpenQuestionFeedbackCopy(
        answer.open_question_feedback?.score,
      );
    }

    if (
      question?.question_type === 'statement' ||
      question?.question_type === 'rating'
    ) {
      return false;
    }

    return (
      answer.is_correct !== null ||
      !!answer.responses?.some((choice) => (choice?.feedback?.length || 0) > 0)
    );
  }

  function updateAnswer(answer?: QuestionAttempt) {
    if (answer) {
      questionAttempts.mutate(
        {...questionAttempts.data, results: [answer]},
        {revalidate: false},
      );

      trackEvent('time_spent_on_question', {
        time: {spent_time: questionTimer.finish()},
        question: {question_attempt_id: answer.id},
        learningPathUserInstance: {
          step_id: learningPathStepId,
          learning_path_user_instance_id: Number(learningPathUserInstanceId),
        },
      });

      if (!answerHasFeedback(answer)) goToNextQuestion();
    }
  }

  // only necessary for ai_tutor learning paths, it defines the first message
  function responseToString() {
    return (
      userChoices
        ?.reduce<string[]>((texts, userChoice) => {
          const questionOption = question?.choices.find(
            (choice) => choice.id === userChoice.option,
          );

          if (questionOption) {
            return [...texts, questionOption.name];
          }
          return texts;
        }, [])
        .join(', ') ?? ''
    );
  }

  function goToNextQuestion() {
    onAdvance?.(responseToString());
    form.reset();
  }

  return (
    <article className={styles.wrapper} aria-live="polite">
      <div className={styles.header}>
        <h1
          aria-label={`${question?.name} ${
            hasBeenAnswered ? 'respondida' : ''
          }`}
        >
          {question?.name}
        </h1>
        <div dangerouslySetInnerHTML={{__html: question?.text || ''}} />
      </div>

      <form onSubmit={form.handleSubmit(handleQuestionUpdate)}>
        {!!question?.choices?.length && (
          <fieldset key={question?.id} className={styles.options}>
            <legend className={styles.srOnly}>Opções</legend>
            {question?.choices?.map((choice) => (
              <Choice
                key={choice.id}
                value={choice.id}
                text={choice.name}
                feedback={
                  userChoices?.find(({option}) => option === choice.id)
                    ?.feedback
                }
                disabled={hasBeenAnswered}
                type={question.question_type}
                isCorrect={
                  userChoices?.find(({option}) => option === choice.id)
                    ?.is_response_correct
                }
                hasBeenAnswered={hasBeenAnswered}
                isLoading={questionAttempts.isLoading}
                hasCorrectAnswer={question.has_correct_answer}
                checked={userChoices?.some(({option}) => option === choice.id)}
                {...form.register('responses')}
              />
            ))}
          </fieldset>
        )}

        {question?.question_type === 'text' && (
          <TextArea
            // The field is intentionally uncontrolled (react-hook-form manages it via ref, not via
            // React's `value`+onChange) — that's why the reset between questions uses
            // `key={question.id}` to force a DOM remount instead of a controlled `value`. The
            // first attempt (`value={userAnswer ?? ''}`) broke typing: since nothing updates
            // `userAnswer` on every keystroke, React kept forcing the field back to the old value
            // on every re-render, fighting with what the student was typing (live finding,
            // 2026-09-22, fixing that same day's earlier finding — text sticking between the 1st
            // and 2nd essay question of the trail).
            key={question?.id}
            maxLength={2000}
            defaultValue={userAnswer}
            placeholder="Resposta"
            readOnly={hasBeenAnswered}
            label="responda com até 2000 caracteres"
            errorMessage={form.formState.errors.answer?.message}
            {...form.register('answer', {
              validate: !hasBeenAnswered
                ? (value) => isFieldEmpty(value)
                : () => undefined,
            })}
          />
        )}

        {question?.question_type === 'rating' && (
          <IconRating
            icon="star"
            options={5}
            answer={userAnswer}
            readOnly={hasBeenAnswered}
            lowerLabel={question.lower_rating_label}
            higherLabel={question.higher_rating_label}
            {...form.register('answer')}
          />
        )}

        {/*  add other types of question here */}

        {hasBeenAnswered && hasGeneralFeedback && (
          <span className={styles.feedback}>{generalFeedback}</span>
        )}

        <div className={styles.controls}>
          <Button
            type="button"
            variant="secondary"
            onClick={onReturn}
            data-testid="return-button"
            disabled={isFirstQuestion}
          >
            Voltar
          </Button>

          <Button
            type="submit"
            variant="primary"
            data-testid="submit-button"
            loading={questionAttempts.isValidating || isSubmitting}
            disabled={
              isSubmitting ||
              questionAttempts.isValidating ||
              (hasFeedback && !hasBeenAnswered && !form.formState.isDirty)
            }
          >
            {question?.button_text || 'Avançar'}
          </Button>
        </div>
      </form>

      <GeneralErrorModal
        id="error"
        title="Erro!"
        showIcon={false}
        subTitle="A etapa anterior não foi concluída"
        description="Você pode ter um questionário e/ou escrita que não foi finalizado. Se você tiver um questionário, verifique e garanta que passou e respondeu todas as questões. Se você tiver uma escrita nessa atividade, garanta que o texto foi enviado"
      />

      <GeneralErrorModal
        id="submitError"
        title="Erro no Envio da Resposta"
        showIcon={false}
        subTitle="Por favor, tente novamente."
        description={`Se o problema persistir:<ul>
<li>Faça logout e feche o navegador completamente.</li>
<li>Entre novamente e tente responder.</li>
<li>Caso a falha continue após essas tentativas, por favor, avise seu professor.<br>Sua escola solicitará que nossa equipe de atendimento investigue o ocorrido.</li>
</ul>`}
      />
    </article>
  );
}

export default Question;
